/**
 * CommentThread (library Calendar / Comment thread): one thread in the rail.
 *
 * A radius 12 card on the surface, an accent line when it is open. The head:
 * the marker (accent tint, the accent itself when open), where, the version
 * and Mark done (a control glass circle). The comment with its Avatar; your
 * own carries a ⋯ menu with Edit and Delete, and Edit rewrites it in place.
 * M7's change when M7 drafted or suggested one (Calendar / M7 change). Replies
 * on a thread line, and the reply field, which can @mention. Each optional
 * piece renders only when the API sent its data (commentCapabilities).
 */
import { useRef, useState, type ReactNode } from "react";
import { ArrowUp, Check, Ellipsis, Pencil, Sparkles, Trash2 } from "lucide-react";
import { KitButton } from "../../components/kit/Button";
import { KitMenu, KitMenuItem, KitMenuPopover, KitMenuTrigger, KitPressable } from "../../components/kit/Menu";
import {
  canMarkDone,
  hasAnchors,
  hasM7Change,
  isDone,
  supportsReplies,
} from "./commentCapabilities";
import {
  commentTime,
  initials,
  isRtlText,
  orgLabel,
  PRIVATE_FROM_BEFORE,
  whereLabel,
  type CommentEntry,
  type CommentThreadData,
  type ThreadM7Change,
} from "./commentModel";
import MentionField, { mentionsIn, type MentionPick } from "./MentionField";
import type { MentionPerson } from "./useMentionPeople";
import "./comments.css";

export function Avatar({ name, size = 24 }: { name: string; size?: 20 | 24 }) {
  return (
    <span className="co-cmt-avatar" data-size={size === 20 ? "20" : undefined} aria-hidden="true">
      {initials(name)}
    </span>
  );
}

/** The body with its @mentions picked out in the accent ink. */
function Body({ entry }: { entry: CommentEntry }) {
  const names = (entry.mentions ?? []).map((m) => `@${m.name}`).filter(Boolean);
  if (!names.length) return <>{entry.body}</>;
  const escaped = names.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).sort((a, b) => b.length - a.length);
  const parts = entry.body.split(new RegExp(`(${escaped.join("|")})`, "g"));
  return (
    <>
      {parts.map((part, index): ReactNode =>
        names.includes(part) ? (
          <span key={index} className="co-cmt-at">
            {part}
          </span>
        ) : (
          part
        ),
      )}
    </>
  );
}

function CommentMenu({ onEdit, onDelete }: { onEdit: () => void; onDelete: () => void }) {
  return (
    <KitMenuTrigger>
      <KitPressable>
        <button type="button" className="co-cmt-more" aria-label="Comment actions">
          <Ellipsis strokeWidth={1.8} aria-hidden="true" />
        </button>
      </KitPressable>
      <KitMenuPopover className="min-w-[160px] w-[160px]">
        <KitMenu
          aria-label="Comment actions"
          onAction={(key) => (key === "edit" ? onEdit() : onDelete())}
        >
          <KitMenuItem id="edit" icon={Pencil} className="text-co-text">
            Edit
          </KitMenuItem>
          <KitMenuItem id="delete" icon={Trash2} danger>
            Delete
          </KitMenuItem>
        </KitMenu>
      </KitMenuPopover>
    </KitMenuTrigger>
  );
}

/** Edit in place: the reply field's frame around the text; Enter saves, Esc cancels. */
function EditField({ entry, onSave, onCancel }: { entry: CommentEntry; onSave: (body: string) => void; onCancel: () => void }) {
  const [draft, setDraft] = useState(entry.body);
  const save = () => {
    const body = draft.trim();
    if (!body) return;
    if (body !== entry.body) onSave(body);
    onCancel();
  };
  return (
    <div className="co-cmt-edit">
      <div className="co-cmt-replyfield" data-focus="true">
        <MentionField
          value={draft}
          onChange={setDraft}
          onSubmit={save}
          onEscape={onCancel}
          people={[]}
          onPick={() => undefined}
          className="co-cmt-textarea"
          ariaLabel="Edit your comment"
          autoFocus
        />
      </div>
      <p className="co-cmt-edit-hint">
        <span>Enter saves · Esc cancels</span>
        <button type="button" className="co-cmt-link" onClick={onCancel}>
          Cancel
        </button>
        <button type="button" className="co-cmt-link co-cmt-link--strong" onClick={save}>
          Save
        </button>
      </p>
    </div>
  );
}

function Comment({
  entry,
  clientName,
  now,
  reply = false,
  onRetry,
  canManage,
  onEdit,
  onDelete,
}: {
  entry: CommentEntry;
  clientName: string | null;
  now: number;
  reply?: boolean;
  onRetry?: () => void;
  /** Your own comment, saved: Edit and Delete. */
  canManage?: boolean;
  onEdit?: (body: string) => void;
  onDelete?: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const rtl = isRtlText(entry.body);
  const deleted = !!entry.deleted;
  const manage = canManage && !deleted && !entry.sendState && !!onEdit && !!onDelete;
  return (
    <div className="co-cmt-comment" data-state={entry.sendState} data-reply={reply || undefined}>
      <div className="co-cmt-byline">
        <Avatar name={entry.authorName} size={reply ? 20 : 24} />
        <span className="co-cmt-name">{entry.authorName.split(/\s+/)[0]}</span>
        <span className="co-cmt-org">{orgLabel(entry.authorType, clientName)}</span>
        {entry.sendState ? null : (
          <time className="co-cmt-time" dateTime={entry.createdAt}>
            {commentTime(entry.createdAt, now)}
            {entry.editedAt && !deleted ? " · edited" : ""}
          </time>
        )}
        {manage && !editing ? <CommentMenu onEdit={() => setEditing(true)} onDelete={() => onDelete?.()} /> : null}
      </div>
      {deleted ? (
        <p className="co-cmt-deleted">Comment deleted</p>
      ) : editing && onEdit ? (
        <EditField entry={entry} onSave={onEdit} onCancel={() => setEditing(false)} />
      ) : (
        <p className="co-cmt-body" dir="auto" data-rtl={rtl || undefined}>
          <Body entry={entry} />
        </p>
      )}
      {/* Creo's read for the team of what the client is asking for (cheap model, team reads only). */}
      {entry.authorType === "CLIENT" && entry.intent && !deleted && !editing ? (
        <p className="co-cmt-intent" dir="auto">
          <Sparkles strokeWidth={1.8} aria-hidden="true" />
          <span className="sr-only">Creo reads this as: </span>
          {entry.intent}
        </p>
      ) : null}
      {/* Written Team only before every comment was shared; the client never gets these. */}
      {entry.visibility === "INTERNAL" ? <p className="co-cmt-private">{PRIVATE_FROM_BEFORE}</p> : null}
      {entry.sendState === "failed" ? (
        <p className="co-cmt-failed" role="alert">
          Not sent
          {onRetry ? (
            <button type="button" className="co-cmt-link" onClick={onRetry}>
              Try again
            </button>
          ) : null}
        </p>
      ) : null}
    </div>
  );
}

/** Only with backend #125 data; the client never sees M7's drafts. */
export function M7Change({
  change,
  onAccept,
  onEdit,
  onDismiss,
  onUndo,
}: {
  change: ThreadM7Change;
  onAccept?: () => void;
  onEdit?: () => void;
  onDismiss?: () => void;
  onUndo?: () => void;
}) {
  if (change.state === "drafting") {
    return (
      <div className="co-cmt-m7" aria-busy="true">
        <span className="co-cmt-m7-label">
          <Sparkles strokeWidth={1.8} aria-hidden="true" />
          Creo is drafting a change
        </span>
        <span className="co-cmt-m7-skeleton" aria-hidden="true" />
        <span className="co-cmt-m7-skeleton is-short" aria-hidden="true" />
      </div>
    );
  }
  if (change.state === "failed") {
    return (
      <div className="co-cmt-m7">
        <span className="co-cmt-m7-label">
          <Sparkles strokeWidth={1.8} aria-hidden="true" />
          {change.label || "Creo could not draft a change"}
        </span>
        <p className="co-cmt-m7-body co-cmt-m7-body--muted">{change.error || "Try again from the chat."}</p>
      </div>
    );
  }
  const suggested = change.state === "suggested";
  return (
    <div className="co-cmt-m7">
      <span className="co-cmt-m7-label">
        <Sparkles strokeWidth={1.8} aria-hidden="true" />
        {change.label}
      </span>
      <p className="co-cmt-m7-body" dir="auto">
        {change.body}
      </p>
      {suggested && (onAccept || onEdit || onDismiss) ? (
        <div className="co-cmt-m7-actions">
          {onAccept ? (
            <KitButton variant="secondary" iconRight={Check} onClick={onAccept}>
              Accept
            </KitButton>
          ) : null}
          {onEdit ? (
            <button type="button" className="co-cmt-link" onClick={onEdit}>
              Edit
            </button>
          ) : null}
          {onDismiss ? (
            <button type="button" className="co-cmt-link" onClick={onDismiss}>
              Dismiss
            </button>
          ) : null}
        </div>
      ) : !suggested && (change.note || onUndo) ? (
        <div className="co-cmt-m7-foot">
          <span>{change.note ?? "Accepted"}</span>
          {onUndo ? (
            <button type="button" className="co-cmt-link" onClick={onUndo}>
              Undo
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** A reply is for everyone, like every comment, and can @mention. */
function ReplyField({
  to,
  authorName,
  phone,
  people,
  onReply,
}: {
  to: string;
  authorName: string;
  /** Phones: 16px text so iOS does not zoom. */
  phone: boolean;
  people: readonly MentionPerson[];
  onReply: (body: string, mentions: MentionPick[]) => void;
}) {
  const [draft, setDraft] = useState("");
  const [picked, setPicked] = useState<MentionPick[]>([]);
  const send = () => {
    const body = draft.trim();
    if (!body) return;
    onReply(body, mentionsIn(body, picked));
    setDraft("");
    setPicked([]);
  };
  return (
    <div className="co-cmt-reply" data-phone={phone || undefined}>
      <div className="co-cmt-replyfield">
        <Avatar name={authorName} size={20} />
        <MentionField
          value={draft}
          onChange={setDraft}
          onSubmit={send}
          people={people}
          onPick={(pick) => setPicked((all) => [...all, pick])}
          className="co-cmt-textarea"
          ariaLabel={`Reply to ${to}`}
          placeholder={`Reply to ${to}…`}
        />
        <button
          type="button"
          className="co-cmt-send"
          data-size="24"
          data-ready={!!draft.trim() || undefined}
          disabled={!draft.trim()}
          aria-label="Send reply"
          onClick={send}
        >
          <ArrowUp strokeWidth={2} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

export interface CommentThreadProps {
  thread: CommentThreadData;
  clientName: string | null;
  /** The clock the times are read against. */
  now: number;
  /** The open thread: selected, or hovered from its page marker. */
  open?: boolean;
  onSelect?: () => void;
  /** The client never sees M7's drafts. */
  audience?: "team" | "client";
  /** A failed local post goes out again. */
  onRetry?: () => void;
  /** Phones: the reply field takes 16px text. */
  phone?: boolean;
  /** Your user id: your own comments get Edit and Delete. */
  userId?: string | null;
  /** Your name, for the reply field's Avatar. */
  authorName?: string;
  /** Who the reply field can @mention. */
  people?: readonly MentionPerson[];
  /** backend #128: only with resolve. */
  onToggleDone?: () => void;
  /** backend #128: only with replies. Every reply is for everyone. */
  onReply?: (body: string, mentions: MentionPick[]) => void;
  /** backend #128: only with anchors. */
  onShowOnPage?: () => void;
  /** backend #128: edit and delete one comment (the thread's or a reply). */
  onEdit?: (entry: CommentEntry, body: string) => void;
  onDelete?: (entry: CommentEntry) => void;
  /** A failed reply goes out again. */
  onRetryReply?: (entry: CommentEntry) => void;
  /** backend #125: M7's change on this comment. */
  onAcceptChange?: () => void;
  onEditChange?: () => void;
  onDismissChange?: () => void;
  onUndoChange?: () => void;
}

export default function CommentThread({
  thread,
  clientName,
  now,
  open = false,
  onSelect,
  audience = "team",
  onRetry,
  phone = false,
  userId = null,
  authorName = "",
  people = [],
  onToggleDone,
  onReply,
  onShowOnPage,
  onEdit,
  onDelete,
  onRetryReply,
  onAcceptChange,
  onEditChange,
  onDismissChange,
  onUndoChange,
}: CommentThreadProps) {
  const ref = useRef<HTMLElement>(null);
  const anchored = hasAnchors(thread);
  const replies = supportsReplies(thread) ? (thread.replies ?? []) : null;
  const done = isDone(thread);
  const showDone = canMarkDone(thread) && !!onToggleDone;
  const showChange = audience === "team" && hasM7Change(thread) && !!thread.m7Change;
  const canReply = replies !== null && !!onReply && !thread.comment.sendState && !thread.comment.deleted;
  const [replying, setReplying] = useState(false);
  const replyOpen = canReply && (open || replying);
  const repliers = replies ? [...new Set(replies.map((reply) => reply.authorName.split(/\s+/)[0]))] : [];
  const where = whereLabel(thread);
  const own = (entry: CommentEntry) =>
    audience === "team" && entry.authorType === "TEAM" && !!userId && entry.authorUserId === userId;
  const lastReplier = replies?.length ? replies[replies.length - 1] : null;
  const replyTo = (lastReplier && !own(lastReplier) ? lastReplier : thread.comment).authorName.split(/\s+/)[0] ?? "";

  return (
    <article
      ref={ref}
      className="co-cmt-thread"
      data-open={open || undefined}
      data-done={done || undefined}
      aria-label={`${where}, ${thread.comment.authorName}`}
      tabIndex={onSelect ? 0 : undefined}
      onClick={(event) => {
        if ((event.target as HTMLElement).closest("button, textarea, a, [role='menu'], [role='listbox']")) return;
        onSelect?.();
      }}
      onKeyDown={(event) => {
        if (event.target === ref.current && (event.key === "Enter" || event.key === " ")) {
          event.preventDefault();
          onSelect?.();
        }
      }}
    >
      <header className="co-cmt-head">
        {anchored && thread.anchor?.number !== undefined ? (
          <span className="co-cmt-marker" aria-hidden="true">
            {thread.anchor.number}
          </span>
        ) : null}
        <span className="co-cmt-where">{where}</span>
        <span className="co-cmt-version">v{thread.version}</span>
        {showDone ? (
          <button
            type="button"
            role="checkbox"
            aria-checked={done}
            aria-label={done ? `Done${thread.resolvedByName ? `, by ${thread.resolvedByName}` : ""}. Reopen` : "Mark done"}
            title={done ? (thread.resolvedByName ? `Done · ${thread.resolvedByName}` : "Done") : "Mark done"}
            className="co-cmt-done co-glass-control"
            onClick={onToggleDone}
          >
            {done ? <Check strokeWidth={2} aria-hidden="true" /> : null}
          </button>
        ) : null}
      </header>

      <Comment
        entry={thread.comment}
        clientName={clientName}
        now={now}
        onRetry={onRetry}
        canManage={own(thread.comment)}
        onEdit={onEdit ? (body) => onEdit(thread.comment, body) : undefined}
        onDelete={onDelete ? () => onDelete(thread.comment) : undefined}
      />

      {showChange && thread.m7Change ? (
        <M7Change
          change={thread.m7Change}
          onAccept={onAcceptChange}
          onEdit={onEditChange}
          onDismiss={onDismissChange}
          onUndo={onUndoChange}
        />
      ) : null}

      {replies && replies.length > 0 ? (
        open ? (
          <div className="co-cmt-replies">
            <ul className="co-cmt-thread-line" aria-label="Replies">
              {replies.map((reply) => (
                <li key={reply.id}>
                  <Comment
                    entry={reply}
                    clientName={clientName}
                    now={now}
                    reply
                    onRetry={reply.sendState === "failed" && onRetryReply ? () => onRetryReply(reply) : undefined}
                    canManage={own(reply)}
                    onEdit={onEdit ? (body) => onEdit(reply, body) : undefined}
                    onDelete={onDelete ? () => onDelete(reply) : undefined}
                  />
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <button type="button" className="co-cmt-link co-cmt-replies-summary" onClick={() => onSelect?.()}>
            {replies.length} {replies.length === 1 ? "reply" : "replies"} · {repliers.join(", ")}
          </button>
        )
      ) : null}

      {replyOpen && onReply ? (
        <ReplyField to={replyTo} authorName={authorName} phone={phone} people={people} onReply={onReply} />
      ) : null}

      {/* An open thread is the reply field's; its links come back when it closes. */}
      {!replyOpen && (canReply || (anchored && onShowOnPage)) ? (
        <div className="co-cmt-links">
          {canReply ? (
            <button
              type="button"
              className="co-cmt-link"
              onClick={() => {
                setReplying(true);
                onSelect?.();
              }}
            >
              Reply
            </button>
          ) : null}
          {anchored && onShowOnPage ? (
            <button type="button" className="co-cmt-link" onClick={onShowOnPage}>
              Show on the page
            </button>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}
